import { useState, useCallback } from 'react';
import { useQuery } from '@apollo/client/react';
import { useOrganizationStore } from '../../../stores/useOrganizationStore';
import { GET_CONTACT_ACTIVITY_FEED } from '../../../lib/graphql/queries/contacts';
import type { GQLActivityFeedItem } from './useContact';

const PAGE_SIZE = 50;

interface GetContactActivityFeedData {
  contact: {
    contactId: string;
    activityFeed: {
      items: GQLActivityFeedItem[];
      total: number;
      hasMore: boolean;
    };
  } | null;
}

export const useContactActivityFeedGQL = (
  contactId: string,
  dateRange?: { from: Date | null; to: Date | null },
  eventTypes?: string[]
) => {
  const { organization } = useOrganizationStore();
  const [currentPage, setCurrentPage] = useState(0);

  const { data, loading, error } = useQuery<GetContactActivityFeedData>(GET_CONTACT_ACTIVITY_FEED, {
    variables: {
      organizationId: organization?.id ?? '',
      contactId,
      limit: PAGE_SIZE,
      offset: currentPage * PAGE_SIZE,
      ...(dateRange?.from && { dateFrom: dateRange.from.toISOString() }),
      ...(dateRange?.to && { dateTo: dateRange.to.toISOString() }),
      ...(eventTypes?.length && { eventTypes }),
    },
    skip: !organization?.id || !contactId,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const items = data?.contact?.activityFeed?.items ?? [];
  const total = data?.contact?.activityFeed?.total ?? 0;
  const hasMore = data?.contact?.activityFeed?.hasMore ?? false;

  const handleNextPage = useCallback(() => {
    setCurrentPage((p) => p + 1);
  }, []);

  const resetPage = useCallback(() => setCurrentPage(0), []);

  return {
    items,
    total,
    hasMore,
    loading,
    error,
    currentPage,
    hasNext: hasMore,
    handleNextPage,
    resetPage,
  };
};
